import type { OrderGroupDetails } from '@/entities/order/model'
import { transitionOrders, useAcknowledgeOrder } from '@/entities/order/lib'
import { useOrderStatusMutation } from '@/entities/order/model'
import { useDisplayStateByStatus } from '@/entities/state/model'

/**
 * Hook to get quick actions for a group of orders.
 * These are presented as buttons on the group order card.
 *
 * @param groupOrder - The group of orders to get quick actions for
 * @returns An object containing the primary action and handlers for it
 */
export function useGroupQuickActions(groupOrder: OrderGroupDetails) {
  const { data: quickButtonStates } = useDisplayStateByStatus()
  const { mutateAsync: updateStatus } = useOrderStatusMutation()
  const { updateOrderAcknowledgement } = useAcknowledgeOrder()

  async function handleAction(action: string) {
    await transitionOrders(groupOrder.orders, action, {
      acknowledge: updateOrderAcknowledgement,
      updateStatus,
    })
  }

  const firstOrder = groupOrder.orders[0]
  const statusDisplayState = quickButtonStates?.[firstOrder.status]
  const primaryAction = statusDisplayState?.quickButtons?.[0]
  const statusColor = statusDisplayState?.color

  async function handlePrimaryAction() {
    if (primaryAction) {
      await handleAction(primaryAction.action)
    }
  }

  return {
    primaryAction,
    handlePrimaryAction,
    handleAction,
    statusColor,
  }
}
